import { App, Avatar, Button, Input, Spin, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import File from 'interfaces/File';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';
import { EventBusEventEnum } from '../../enums/EventBusEventEnum';
import { FileSalesStatusEnum } from '../../enums/FileSalesStatusEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { useAuth } from '../../hooks/useAuth';
import useFilesActions from '../../hooks/useFilesActions';
import useOpenFile from '../../hooks/useOpenFile';
import { useTags } from '../../hooks/useTags';
import FileAccess from '../../interfaces/FileAccess';
import { GetFileAccessesPayload } from '../../interfaces/FileService';
import EventBus from '../../services/EventBus';
import { FileService } from '../../services/fileService';
import { dateFromTicks, detectFileType } from '../../services/utils';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';
import {
  copyToClipboard,
  getFolderHex,
  getPurchaseLink,
  printAmount,
  printCurrency,
  removeShortcutExtension,
} from '../../utils/utils';
import FileIcon from '../Common/FileIcon';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import { Icon } from '../Core/Icon';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

interface FileDetailTabProps {
  file: File;
}

const DESCRIPTION_CHARACTERS_LIMIT = 10000;

const fileService = new FileService();

const FileDetailTab: React.FC<FileDetailTabProps> = ({ file }) => {
  const { t } = useTranslation();
  const { notification, message } = App.useApp();
  const { authenticatedUser, onBehalfOfUser, isInFileRoute } = useAuth();
  const { handleShareFiles } = useFilesActions({
    folderUid: file.parentId?.toString(),
  });
  const { openFileSafely } = useOpenFile();
  const [isLoading, setIsLoading] = useState(true);
  const [fileAccesses, setFileAccesses] = useState<FileAccess[]>([]);
  const [totalFileAccesses, setTotalFileAccesses] = useState(0);
  const [fileDescription, setFileDescription] = useState('');
  const [fileSellInfo, setFileSellInfo] = useState<null | {
    price: number;
    revenue: number;
    unitsSold: number;
  }>(null);
  const debounceTextareaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const matchFilesSection = useMatch('/files/:section/*');
  const navigate = useNavigate();
  const currentSection = matchFilesSection?.params?.section || 'personal';
  const { tags } = useTags();

  const isFileMine = function (file: File) {
    return file.ownerId === authenticatedUser?.userId;
  };

  /**
   * Retrieves the file access details for a specified file and updates
   * the corresponding lists of users and groups who have access.
   * Utilizes a payload with the file's ID to fetch access information.
   * The results are separated into users and groups, which are then
   * updated in their respective states using setUsers and setGroups.
   * If an error occurs, a notification is created.
   *
   * Dependencies:
   * - file: The file object containing the fileId for which access details are required.
   * - fileService: Service to fetch file access details.
   * - setUsers: Function to update the list of users with file access.
   * - setGroups: Function to update the list of groups with file access.
   * - createNotification: Function to create notifications for the user.
   * - t: Translation function for error messages.
   */
  const getFileAccesses = useCallback(async () => {
    const payload: GetFileAccessesPayload = {
      files: [file?.fileId],
      offset: 0,
      count: 4,
    };

    if (onBehalfOfUser) {
      payload.onBehalfOf = onBehalfOfUser.userId;
    }

    fileService
      .getFileAccesses(payload)
      .then((response) => {
        setFileAccesses(response.accesses);
        setTotalFileAccesses(response.total);
      })
      .catch((error) => {
        notification.error({
          message: t('errors.hitASnag'),
        });
      });
  }, [file, notification, t]);

  /**
   * Retrieves the file information for a specified file and updates
   * the corresponding state with the received data.
   * Utilizes a payload with the file's ID to fetch information.
   * The results are then updated in the state variable using setFileInfo.
   * If an error occurs, a notification is created.
   */

  const getFile = useCallback(async () => {
    if (!file) {
      setIsLoading(false);
      return;
    }

    fileService
      .getFile(file.uid)
      .then((response) => {
        // Set file description
        setFileDescription(response?.description);
        setFileSellInfo(response);
      })
      .catch((error) => {
        notification.error({
          message: t('errors.genericFetchError', {
            resource: t('common.file'),
          }),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [file, notification, t]);

  /**
   * Emits an event to notify that the file has been updated.
   * @param updatedFiles - The updated file object.
   */
  const onFileUpdated = (updatedFiles: File[]) => {
    EventBus.emit(EventBusEventEnum.OnFilesUpdated, {
      files: updatedFiles,
    });
  };

  /**
   * Updates the file description with user input while implementing a debounce mechanism
   * and limiting the description length to a predefined limit. Simultaneously updates the
   * file description in the server and the local file list while handling success and
   * error notifications.
   *
   * This function performs the following:
   * - Extracts the value from the event target.
   * - Checks if the value's length is within the allowed character limit.
   * - Cancels any existing debounce timeout for description change.
   * - Sets a new debounce timeout for sending the*/
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (!isFileMine(file)) {
      return;
    }

    const { value } = e.target;

    if (value.length <= DESCRIPTION_CHARACTERS_LIMIT) {
      setFileDescription(value);

      // Delete previous debounce if exists
      if (debounceTextareaTimeoutRef.current) {
        clearTimeout(debounceTextareaTimeoutRef.current);
      }

      // Set new debounce
      debounceTextareaTimeoutRef.current = setTimeout(() => {
        fileService
          .updateFileDescription(file.uid, value)
          .then(() => {
            message.success(
              t('notifications.genericUpdateSuccess', {
                resource: t('common.description'),
              })
            );
          })
          .catch(() => {
            notification.error({
              message: t('errors.genericUpdateError', {
                resource: t('common.description'),
              }),
            });
          });
      }, 500);
    }
  };

  /**
   * Handles the opening of a file based on the current route and file type.
   *
   * This function checks the type of the file and the current route to determine
   * the appropriate action to take. It navigates to the folder view if the file is a folder
   * and the current route is not the trash view. If the file is in the trash view,
   * it restores the file. If the file is in the shared view, it opens the file.
   *
   * @param file - The file to be opened.
   */
  const handleOpenFile = (file: File) => {
    if (file.isFolder && !isInFileRoute(FilesTableEnum.Trash)) {
      navigate(`/files/${currentSection}/folder/${file.uid}`, {
        replace: true,
      });
    } else {
      openFileSafely(file);
    }
  };

  /**
   * Indicates whether the "Manage Access" option should be visible to the user.
   *
   * This value is determined by the following conditions:
   * - The current list of filters does not represent a trash view, as indicated by the
   *   `isTrash(filters.list)` function.
   * - The user identified by `authenticatedUser` is the owner of the `file`, checked by comparing
   *   `file?.ownerId` with `authenticatedUser?.userId`.
   *
   * This value is memoized to optimize performance and prevent unnecessary recalculations, updating
   * only when any of the dependencies (`filters.list`, `file`, or `authenticatedUser`) change.
   */
  const isManageAccessVisible = useMemo(() => {
    return (
      !isInFileRoute(FilesTableEnum.Trash) &&
      file?.ownerId === authenticatedUser?.userId &&
      !file?.isQuickShare
    );
  }, [file, authenticatedUser]);

  useEffect(() => {
    setFileAccesses([]);
    if (file && !isInFileRoute(FilesTableEnum.Trash)) {
      const promises = [getFile()];

      if (isFileMine(file)) {
        promises.push(getFileAccesses());
      }

      Promise.all(promises).then(() => {});
    } else {
      getFile().then(() => {});
    }
    setFileDescription(file?.description);
  }, [file]);

  return (
    <OverlayScrollbarsComponent
      options={{
        scrollbars: {
          autoHide: 'leave',
        },
      }}
      defer
      style={{ height: '100%', overflow: 'auto' }}
    >
      <div className="flex h-full flex-col gap-6 pr-[10px]">
        {/* File Details */}
        <dl className="grid grid-cols-1 gap-2 rounded-md bg-azure-light p-3">
          <div className="grid grid-cols-2 gap-2">
            <dt className="text-right font-medium">
              {t('common.creationDate')}:
            </dt>
            <dd className="text-left">
              {dateFromTicks(file?.createdAt).toLocaleDateString() || '-'}
            </dd>
          </div>
          <div className="grid grid-cols-2 items-start gap-2">
            <dt className="text-right font-medium">File ID:</dt>
            <dd className="text-left">
              <Tooltip title={t('common.copyToClipboard')}>
                <Button
                  type="link"
                  size="small"
                  className="h-[22px] gap-2 p-0"
                  onClick={() => {
                    copyToClipboard(file?.fileId.toString() || '');
                  }}
                >
                  <span className="max-w-24 truncate">
                    {file?.fileId || '-'}
                  </span>
                  <Icon name="CopyIcon" className="w-4" />
                </Button>
              </Tooltip>
            </dd>
            <dt className="text-right font-medium">UID:</dt>
            <dd className="text-left">
              <Tooltip title={t('common.copyToClipboard')}>
                <Button
                  type="link"
                  size="small"
                  className="h-[22px] gap-2 p-0"
                  onClick={() => {
                    copyToClipboard(file?.uid.toString() || '');
                  }}
                >
                  <span>{file?.uid || '-'}</span>
                  <Icon name="CopyIcon" className="w-4" />
                </Button>
              </Tooltip>
            </dd>
          </div>
          {!file.isFolder && (
            <div className="grid grid-cols-2 gap-2">
              <dt className="text-right font-medium">
                {t('common.fileType')}:
              </dt>
              <dd className="text-left">
                {detectFileType(removeShortcutExtension(file.fileExtension))}
              </dd>
            </div>
          )}
        </dl>
        {/* File Tag */}
        <div className={file.tags.length === 0 ? 'hidden' : 'flex'}>
          {file.tags.map((tagIg) => {
            const tag = tags.find((t) => t.id === tagIg);
            if (tag) {
              return (
                <Tag
                  key={tag.id}
                  style={{
                    backgroundColor: getContrastBackgroundColorForText(
                      tag.color
                    ),
                  }}
                >
                  <div className="flex flex-row items-center justify-center lg:justify-between">
                    <div />
                    <span className="max-w-28" style={{ color: tag.color }}>
                      <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                    </span>
                    {tag.publicTag ? (
                      <Icon
                        name="BuildingIcon"
                        className="h-4 w-4 text-gray-dark"
                      />
                    ) : (
                      <div className="w-1" />
                    )}
                  </div>
                </Tag>
              );
            }
          })}
        </div>

        {/* File Icon */}
        <div className="dashed flex w-full items-center justify-center rounded-md border border-gray-dark p-2">
          <FileIcon
            className="h-40"
            isFolder={file.isFolder}
            isShortcut={file.isShortcut}
            fileExtension={removeShortcutExtension(file.fileExtension)}
            showExtension={true}
            shortcutIconClassName="h-10 w-10 bottom-0 left-8"
            style={{
              color: getFolderHex(file),
            }}
          />
        </div>

        {/* File Sell */}
        {isFileMine(file) &&
          (file.salesStatus === FileSalesStatusEnum.ForSale ||
            file.salesStatus === FileSalesStatusEnum.OnHold) &&
          fileSellInfo && (
            <Spin spinning={isLoading}>
              <div className="flex flex-col gap-2">
                <label className="text-lg font-medium">
                  {t('store.file_sales_overview')}
                </label>

                <div className="border-gray-200 dark:bg-gray-900 dark:border-gray-700 rounded-md border bg-white p-4 shadow-md">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex flex-col">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('store.total_sales')}
                      </span>
                      <span className="text-l font-semibold">
                        {fileSellInfo.unitsSold || 0}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('store.file_revenue')}
                      </span>
                      <span className="text-l dark:text-green-400 font-semibold text-green">
                        {printCurrency(file.currencyPrice)}
                        {printAmount(fileSellInfo.revenue as number)}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('store.unit_price')}
                      </span>
                      <span className="text-l font-semibold">
                        {printCurrency(file.currencyPrice)}
                        {printAmount(fileSellInfo.price as number)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="self-end px-2">
                  <a
                    href={getPurchaseLink(file.uid)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-align-right text-blue hover:underline"
                  >
                    {t('store.open_store_link')}
                  </a>
                </div>
              </div>
            </Spin>
          )}

        {/* File Accesses */}
        {isFileMine(file) && (
          <div className="flex h-36 flex-col gap-3">
            <div className="flex flex-row items-start justify-between gap-2">
              <span className="text-lg font-medium">
                {t('common.sharedItems')}
              </span>
              {isManageAccessVisible && (
                <Button
                  type="text"
                  className="underline"
                  onClick={() => {
                    handleShareFiles([file], {
                      onFilesUpdated: onFileUpdated,
                    });
                  }}
                >
                  {t('common.manageAccess')}
                </Button>
              )}
            </div>
            <Spin spinning={isLoading} tip="Loading info...">
              <div
                className={clsx(
                  'grid grid-cols-4 gap-2 transition-all duration-200 ease-in-out',
                  isLoading && 'blur-sm'
                )}
              >
                {fileAccesses?.length === 0 ? (
                  <div className="col-span-4 text-sm text-gray-dark">
                    {t('common.notSharedYet')}
                  </div>
                ) : (
                  fileAccesses.slice(0, 3).map((fileAccess) => (
                    <div
                      key={fileAccess.id}
                      className="flex flex-col items-center"
                    >
                      <Avatar
                        size={64}
                        className={clsx(
                          'bg-azure-hover',
                          isLoading && 'blur-sm'
                        )}
                      >
                        {fileAccess.user ? (
                          <span className="uppercase">
                            {fileAccess.user?.firstName
                              ? `${fileAccess.user?.firstName.charAt(0)}${fileAccess.user?.lastName.charAt(0)}`
                              : fileAccess.user?.email?.charAt(0) || ''}
                          </span>
                        ) : (
                          <Icon name="UsersIcon" />
                        )}
                      </Avatar>
                      <p
                        className={clsx(
                          'mt-1 max-w-20 truncate text-center text-sm transition-all duration-200',
                          isLoading && 'blur-sm'
                        )}
                      >
                        <Tooltip
                          title={`${fileAccess.user?.firstName ? `${fileAccess.user?.firstName} ${fileAccess.user?.lastName}` : fileAccess.user?.email || fileAccess.group?.name}`}
                        >
                          {fileAccess.user?.firstName
                            ? `${fileAccess.user?.firstName} ${fileAccess.user?.lastName}`
                            : fileAccess.user?.email || fileAccess.group?.name}
                        </Tooltip>
                      </p>
                    </div>
                  ))
                )}

                {fileAccesses?.length > 3 && (
                  <Tooltip
                    title={
                      <div className="flex flex-col items-center">
                        {fileAccesses.slice(3).map((fileAccess) => (
                          <div key={fileAccess.id}>
                            {fileAccess.user?.firstName ||
                              fileAccess.group?.name}
                          </div>
                        ))}
                        {fileAccesses?.length > 3 && (
                          <>and {totalFileAccesses - 4} more</>
                        )}
                      </div>
                    }
                  >
                    <Avatar
                      className="bg-azure-hover"
                      size={64}
                      icon={
                        <span className="text-xl">{`+${totalFileAccesses - 3}`}</span>
                      }
                    />
                  </Tooltip>
                )}
              </div>
            </Spin>
          </div>
        )}

        {/* File Description */}
        <div className="flex flex-col gap-2">
          <label className="text-lg font-medium">
            {t('common.description')}
          </label>
          <Input.TextArea
            disabled={!isFileMine(file)}
            rows={4}
            maxLength={DESCRIPTION_CHARACTERS_LIMIT}
            value={fileDescription}
            onChange={handleDescriptionChange}
          />
          <p className="text-right text-sm text-gray-dark">
            {fileDescription?.length} / {DESCRIPTION_CHARACTERS_LIMIT}
          </p>
        </div>
        <div className="pb-4">
          <Button
            type="primary"
            size="large"
            className="w-full"
            onClick={() => {
              handleOpenFile(file);
            }}
          >
            {t('common.open')}
          </Button>
        </div>
      </div>
    </OverlayScrollbarsComponent>
  );
};

export default FileDetailTab;
